import { useEffect, useState } from 'react'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import { getServerStatus } from './api/auth'
import { getInviteInfo, claimInvite } from './api/invites'
import { followUser } from './api/users'
import { Shell } from './components/Layout/Shell'
import { Daily } from './pages/Daily'
import { EntryView } from './pages/EntryView'
import { Feed } from './pages/Feed'
import { Login } from './pages/Login'
import { PublicFeed } from './pages/PublicFeed'
import { Register } from './pages/Register'
import { Onboarding } from './pages/Onboarding'
import { Pins } from './pages/Pins'
import { Profile } from './pages/Profile'
import { Settings } from './pages/Settings'
import { TagsetView } from './pages/TagsetView'
import { AddPasskey } from './pages/AddPasskey'
import { VerifyEmail } from './pages/VerifyEmail'
import { Tagsets } from './pages/Tagsets'
import { TagView } from './pages/TagView'
import { Tags } from './pages/Tags'
import { Welcome } from './pages/Welcome'
import { Archive } from './pages/Archive'
import { MediaLibrary } from './pages/MediaLibrary'
import { Admin } from './pages/Admin'
import { Notifications } from './pages/Notifications'
import { Todos } from './pages/Todos'
import { useAuthStore } from './store/auth'
import { useThemeStore } from './store/theme'
import { useFaviconSync } from './hooks/useFaviconSync'
import './styles/base.css'

// Checks /auth/status on every initial mount. Redirects to /onboarding
// if the server isn't configured yet; otherwise renders child routes.
function ConfigGuard() {
  const [configured, setConfigured] = useState<boolean | null>(null)
  const setEmailEnabled = useAuthStore((s) => s.setEmailEnabled)

  useEffect(() => {
    getServerStatus()
      .then((s) => {
        setConfigured(s.configured)
        setEmailEnabled(s.email_enabled)
      })
      .catch(() => setConfigured(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (configured === null) return <div style={{ padding: '2rem' }}>loading…</div>
  if (!configured) return <Navigate to="/onboarding" replace />
  return <Outlet />
}

function Home() {
  const token = useAuthStore((s) => s.token)
  const username = useAuthStore((s) => s.username)
  if (token && username) return <Navigate to={`/~${username}/journal`} replace />
  return <PublicFeed />
}

// Detects ?invite=TOKEN on load, stores it in sessionStorage, then after login
// shows a prompt to optionally follow the inviter and claims the invite.
function InviteClaimHandler() {
  const token = useAuthStore((s) => s.token)
  const [pendingInvite, setPendingInvite] = useState<{ token: string; inviterUsername: string } | null>(null)
  const [claimed, setClaimed] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const inviteToken = params.get('invite')
    if (inviteToken) {
      sessionStorage.setItem('pendingInviteToken', inviteToken)
      params.delete('invite')
      const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '')
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  useEffect(() => {
    if (!token || claimed) return
    const storedToken = sessionStorage.getItem('pendingInviteToken')
    if (!storedToken) return

    getInviteInfo(storedToken)
      .then((info) => setPendingInvite({ token: storedToken, inviterUsername: info.inviter_username }))
      .catch(() => sessionStorage.removeItem('pendingInviteToken'))
  }, [token, claimed])

  if (!pendingInvite) return null

  async function handleFollow() {
    if (!pendingInvite) return
    try {
      await followUser(pendingInvite.inviterUsername)
      await claimInvite(pendingInvite.token)
    } catch { /* ignore */ }
    sessionStorage.removeItem('pendingInviteToken')
    setPendingInvite(null)
    setClaimed(true)
  }

  async function handleSkip() {
    if (!pendingInvite) return
    try { await claimInvite(pendingInvite.token) } catch { /* ignore */ }
    sessionStorage.removeItem('pendingInviteToken')
    setPendingInvite(null)
    setClaimed(true)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-fg)', padding: '2rem', maxWidth: 360, width: '90%' }}>
        <p style={{ margin: '0 0 1.25rem' }}>
          follow <strong>{pendingInvite.inviterUsername}</strong>, who invited you?
        </p>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button type="button" onClick={handleFollow} style={{ flex: 1 }}>yes</button>
          <button type="button" onClick={handleSkip} style={{ flex: 1 }}>no</button>
        </div>
      </div>
    </div>
  )
}

// Single stable router — created once, never recreated on re-renders.
// ConfigGuard and Shell are both pathless layouts; all page routes carry
// explicit absolute paths so React Router matching is unambiguous.
const router = createBrowserRouter([
  { path: '/onboarding', element: <Onboarding /> },
  {
    element: <ConfigGuard />,
    children: [
      {
        element: <Shell />,
        children: [
          { path: '/', element: <Home /> },
          { path: '/login', element: <Login /> },
          { path: '/register', element: <Register /> },
          { path: '/welcome', element: <Welcome /> },
          { path: '/settings', element: <Settings /> },
          { path: '/notifications', element: <Notifications /> },
          { path: '/verify-email', element: <VerifyEmail /> },
          { path: '/add-passkey', element: <AddPasskey /> },
          { path: '/feed', element: <Feed /> },
          { path: '/:username', element: <Profile /> },
          { path: '/:username/journal', element: <Daily /> },
          { path: '/:username/entry/*', element: <EntryView /> },
          { path: '/:username/pins', element: <Pins /> },
          { path: '/archive', element: <Archive /> },
          { path: '/:username/media', element: <MediaLibrary /> },
          { path: '/admin', element: <Admin /> },
          { path: '/:username/tagsets', element: <Tagsets /> },
          { path: '/:username/tagsets/:tagsetName', element: <TagsetView /> },
          { path: '/:username/tags', element: <Tags /> },
          { path: '/:username/tags/:tagName', element: <TagView /> },
          { path: '/:username/todos', element: <Todos /> },
        ],
      },
    ],
  },
])

function App() {
  const { userTheme, setUserTheme } = useThemeStore()
  useFaviconSync()

  // Re-apply persisted user theme on mount
  useEffect(() => {
    if (userTheme) setUserTheme(userTheme)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <RouterProvider router={router} />
      <InviteClaimHandler />
    </>
  )
}

export default App
